import { Check, Circle, Eye, EyeOff } from 'lucide-react';
import { useId } from 'react';

import { cn } from '@/design-system/lib/cn';
import { Input, type InputProps } from '@/design-system/ui/Input';
import {
  passwordRules,
  passwordStrength,
  STRENGTH_LABEL,
  type PasswordStrength,
} from '@escala/domain/password';

/**
 * Campo de senha com "Mostrar senha". `shown` fica com quem usa, para um botão só mostrar
 * a senha e a confirmação ao mesmo tempo (como no protótipo).
 */
export function PasswordInput({
  shown,
  onToggle,
  className,
  ...props
}: Omit<InputProps, 'type'> & { shown: boolean; onToggle?: () => void }) {
  const label = shown ? 'Ocultar senha' : 'Mostrar senha';
  const Icon = shown ? EyeOff : Eye;

  return (
    <span className="relative flex w-full items-center">
      <Input type={shown ? 'text' : 'password'} className={cn(onToggle && 'pr-12', className)} {...props} />
      {onToggle ? (
        <button
          type="button"
          onClick={onToggle}
          aria-label={label}
          title={label}
          className="absolute right-1 grid h-9 w-10 place-items-center rounded-control-sm text-ink-subtle hover:bg-surface-muted"
        >
          <Icon aria-hidden className="size-4 stroke-[1.8]" />
        </button>
      ) : null}
    </span>
  );
}

const BAR: Record<PasswordStrength, string> = {
  0: 'bg-line-divider',
  1: 'bg-danger',
  2: 'bg-status-partial-bar',
  3: 'bg-status-complete-bar',
  4: 'bg-success',
};

const LABEL_COLOR: Record<PasswordStrength, string> = {
  0: 'text-ink-subtle',
  1: 'text-status-conflict',
  2: 'text-status-partial',
  3: 'text-status-complete',
  4: 'text-success',
};

/**
 * Medidor de força (4 barras, a partir do primeiro caractere) e a lista de requisitos
 * obrigatórios, cada um marcado quando cumprido — as regras de `@escala/domain/password`.
 */
export function PasswordChecklist({ password }: { password: string }) {
  const strength = passwordStrength(password);
  const id = useId();

  return (
    <div className="flex flex-col gap-2">
      {strength > 0 ? (
        <div className="flex items-center gap-2">
          <div aria-hidden className="grid flex-1 grid-cols-4 gap-1">
            {[1, 2, 3, 4].map((n) => (
              <span key={n} className={cn('h-1 rounded-pill', n <= strength ? BAR[strength] : BAR[0])} />
            ))}
          </div>
          <span
            id={id}
            className={cn('min-w-10 text-right text-caption font-semibold', LABEL_COLOR[strength])}
          >
            <span className="sr-only">Força da senha: </span>
            {STRENGTH_LABEL[strength as Exclude<PasswordStrength, 0>]}
          </span>
        </div>
      ) : null}
      <ul aria-label="Requisitos da senha" className="m-0 flex list-none flex-col gap-1 p-0">
        {passwordRules(password).map((rule) => (
          <li
            key={rule.label}
            className={cn(
              'flex items-center gap-1.5 text-caption',
              rule.ok ? 'text-success' : 'text-ink-subtle'
            )}
          >
            {rule.ok ? (
              <Check aria-hidden className="size-3.5 stroke-[2.4]" />
            ) : (
              <Circle aria-hidden className="size-3.5 stroke-2" />
            )}
            {rule.label}
            <span className="sr-only">{rule.ok ? ' (cumprido)' : ' (pendente)'}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
